import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import NavBar from "../components/navigation/navBar";
import Sidebar from "../components/navigation/Sidebar";
import { get_datos_oferta_erp } from "../services/ofertas";
import LoadingBar from "../components/Loader";
import Footer from "../components/navigation/Footer";
import ReleaseNotesModal from "../components/modals/ReleaseNotesModal";
import { resourceService } from "../services/resourceService";

const Layout = (props) => {
  const {
    children,
    offerContext,
    isAuthenticated,
    user,
  } = props;
  const location = useLocation();
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [sidebarOferta, setSidebarOferta] = useState(null);
  const [releaseModal, setReleaseModal] = useState(null);

  // Define public routes that don't need the sidebar/navbar
  const isPublicRoute = useMemo(() => {
    const publicPaths = [
      "/login",
      "/reset_password",
      "/activate",
      "/password/reset/confirm"
    ];
    return publicPaths.some(path => location.pathname.startsWith(path));
  }, [location.pathname]);

  // Load offer data when offerContext changes
  useEffect(() => {
    const fetchSidebarOferta = async () => {
      if (offerContext?.oferta_id) {
        try {
          const data = await get_datos_oferta_erp(offerContext.oferta_id);
          setSidebarOferta(data);
        } catch (error) {
          console.error("Error loading sidebar offer data:", error);
        }
      } else {
        setSidebarOferta(null);
      }
    };

    if (isAuthenticated && !isPublicRoute) {
      fetchSidebarOferta();
    }
  }, [offerContext?.oferta_id, isAuthenticated, isPublicRoute]);

  // Check for new release notes to show as modal (once per version per user)
  useEffect(() => {
    if (!isAuthenticated || isPublicRoute) return;
    const checkRelease = async () => {
      try {
        const release = await resourceService.getLatestRelease();
        if (!release || !release.version) return;
        const userId = user?.id || "anon";
        const storageKey = `seen_release_${userId}`;
        const seen = localStorage.getItem(storageKey);
        if (seen !== release.version) {
          setReleaseModal(release);
        }
      } catch {
        // Silently ignore: non-critical
      }
    };
    checkRelease();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isPublicRoute]);

  const handleCloseReleaseModal = () => {
    if (releaseModal?.version) {
      const userId = user?.id || "anon";
      localStorage.setItem(`seen_release_${userId}`, releaseModal.version);
    }
    setReleaseModal(null);
  };

  const toggleSidebar = () => setShowMobileSidebar(!showMobileSidebar);
  const closeSidebar = () => setShowMobileSidebar(false);

  const [showOverlay, setShowOverlay] = useState(false);
  const activeErrorToastsRef = useRef(new Set());

  useEffect(() => {
    const unsubscribe = toast.onChange((event) => {
      const { status, type, id } = event;

      if (status === "added") {
        //si es una nova notificacio succes es temporal
        if (type === "success") {
          toast.update(id, { autoClose: 5000 });
          return;
        }
        //si es una nova notificacio error es permanent i mostra overlay
        if (type === "error") {
          activeErrorToastsRef.current.add(id);
          setShowOverlay(true);
        }
      }

      //si tanquem notifiacio error amaguem overlay
      if (status === "removed" && type === "error") {
        activeErrorToastsRef.current.delete(id);
        setShowOverlay(activeErrorToastsRef.current.size > 0);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // If it's a public route or not authenticated, only show the children (and toast)
  if (isPublicRoute || !isAuthenticated) {
    return (
      <div className="min-vh-100 bg-light">
        {showOverlay && <div className="toast-overlay" />}
        <ToastContainer
          autoClose={false}
          closeOnClick={false}
          draggable={false}
          closeButton={true}
          limit={3}
          newestOnTop
          pauseOnFocusLoss
          pauseOnHover
          theme="light"
          className="custom-toast-container"
          position="top-center"
        />
        {children}
      </div>
    );
  }

  return (
    <div className="d-flex min-vh-100 bg-light">
      {showOverlay && <div className="toast-overlay" />}
      <ToastContainer
        autoClose={false}
        closeOnClick={false}
        draggable={false}
        closeButton={true}
        limit={3}
        newestOnTop
        pauseOnFocusLoss
        pauseOnHover
        theme="light"
        className="custom-toast-container"
        position="top-center"
      />

      {/* Sidebar - Desktop (Static) */}
      <div className="d-none d-md-block border-end bg-white" style={{ width: "280px", flexShrink: 0 }}>
        <Sidebar offerContext={props.offerContext} sidebarOferta={sidebarOferta} />
      </div>

      {/* Sidebar - Mobile (Offcanvas) */}
      <Offcanvas show={showMobileSidebar} onHide={closeSidebar}>
        <Offcanvas.Body className="p-0">
          <Sidebar closeMobileSidebar={closeSidebar} offerContext={props.offerContext} sidebarOferta={sidebarOferta} />
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Layout Area */}
      <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
        <NavBar title={props.title} onToggleSidebar={toggleSidebar} />
        <LoadingBar />

        {/* Content */}
        <div className="p-0 flex-grow-1 position-relative" style={{ overflowY: "auto" }}>
          {children}
        </div>

        {/* Restore Footer */}
        <Footer />
      </div>

      {/* Release Notes Modal - shown once per version per user */}
      <ReleaseNotesModal release={releaseModal} onClose={handleCloseReleaseModal} />
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.Auth.isAuthenticated,
  user: state.Auth.user,
});

export default connect(mapStateToProps, {
})(Layout);
